import React, { Component } from 'react';
import { func, number, object, string } from 'prop-types';
import classNames from 'classnames';
import config from '../../config';
import { isAnyFilterActive } from '../../util/search';
import { propTypes } from '../../util/types';
import { InboxFiltersMobile, InboxFiltersPrimary, SortBy } from '../../components';
import FilterComponent from '../SearchPage/FilterComponent';
import { validFilterParams } from '../SearchPage/SearchPage.helpers';
import isEqual from 'lodash/isEqual';

import css from './InboxFilterPanel.css';

// Primary filters have their content in dropdown-popup.
// With this offset we move the dropdown to the left a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;


class InboxFiltersPanel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentQueryParams: props.activeQueryParams,
      tab: props.tab,
    };

    this.applyFilters = this.applyFilters.bind(this);
    this.cancelFilters = this.cancelFilters.bind(this);
    this.resetAll = this.resetAll.bind(this);

    this.initialValues = this.initialValues.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);

    // SortBy
    this.handleSortBy = this.handleSortBy.bind(this);
  }

  componentDidUpdate(prevProps, prevState) {
    if (!isEqual(prevState.currentQueryParams, this.props.activeQueryParams)) {
      this.setState({ currentQueryParams: this.props.activeQueryParams });
    }
  }

  applyFilters() {
    const { activeQueryParams } = this.props;
    const searchParams = { ...activeQueryParams, ...this.state.currentQueryParams };

    this.props.filterTransactions(searchParams);
  }

  // Close the filters by clicking cancel, revert to the initial params
  cancelFilters() {
    this.setState({ currentQueryParams: {} });
  }

  // Reset all filter query parameters
  resetAll(e) {
    this.setState({ currentQueryParams: {} });
    this.props.resetFilter();
  }

  initialValues(queryParamNames) {
    const activeQueryParams = this.props.activeQueryParams;

    // Query parameters that are in state (user might have not yet clicked "Apply")
    const currentQueryParams = this.state.currentQueryParams;

    // Get initial value for a given parameter from state if its there.
    const getInitialValue = paramName => {
      const currentQueryParam = currentQueryParams[paramName];
      const hasQueryParamInState = typeof currentQueryParam !== 'undefined';
      const isKeywordParam = paramName === 'keywords';
      return isKeywordParam
        ? activeQueryParams[paramName]
        : hasQueryParamInState
        ? currentQueryParam
        : activeQueryParams[paramName];
    };

    // Return all the initial values related to given queryParamNames
    // InitialValues for "amenities" filter could be
    // { amenities: "has_any:towel,jacuzzi" }
    const isArray = Array.isArray(queryParamNames);
    return isArray
      ? queryParamNames.reduce((acc, paramName) => {
          return { ...acc, [paramName]: getInitialValue(paramName), allValues: currentQueryParams };
        }, {})
      : {};
  }

  getHandleChangedValueFn() {
    const { activeQueryParams } = this.props;

    return updatedURLParams => {
      const updater = prevState => {
        const mergedQueryParams = { ...activeQueryParams, ...prevState.currentQueryParams };

        return {
          currentQueryParams: {
            ...mergedQueryParams,
            ...updatedURLParams,
          },
        };
      };

      const callback = () => {
        const searchParams = this.state.currentQueryParams;
        const { sort, ...searchWithoutSort } = searchParams;
        this.props.filterItems(searchWithoutSort);
      };

      this.setState(updater, callback);
    };
  }

  handleSortBy(urlParam, values) {
    this.props.sortItems(values);
  }

  render() {
    const {
      className,
      rootClassName,
      activeQueryParams,
      onManageDisableScrolling,
      onOpenModal,
      onCloseModal,
      inboxItemsAreLoaded,
      showAsModalMaxWidth,
      filterConfig,
      sortConfig,
      resultsCount,
      totalResultsCount,
    } = this.props;

    const primaryFilters = filterConfig.filter(f => f.group === 'primary');

    // Selected aka active filters
    const selectedFilters = validFilterParams(activeQueryParams, filterConfig);
    const selectedFiltersCount = Object.keys(selectedFilters).length;

    const sortBy = mode => {
      const conflictingFilterActive = isAnyFilterActive(
        sortConfig.conflictingFilters,
        activeQueryParams,
        filterConfig
      );

      const mobileClassesMaybe =
        mode === 'mobile'
          ? {
              rootClassName: css.sortBy,
              menuLabelRootClassName: css.sortByMenuLabel,
            }
          : {};
      return sortConfig.active ? (
        <SortBy
          {...mobileClassesMaybe}
          sort={activeQueryParams[sortConfig.queryParamName]}
          isConflictingFilterActive={!!conflictingFilterActive}
          onSelect={this.handleSortBy}
          showAsPopup
          contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
          sortConfig={sortConfig}
          defaultValue={this.props.activeQueryParams.sort}
        />
      ) : null;
    };

    const classes = classNames(rootClassName || css.inboxMessagesContainer, className);

    return (
      <div className={classes}>
        <InboxFiltersPrimary
          className={css.inboxFiltersPrimary}
          sortByComponent={sortBy('desktop')}
          resultsCount={resultsCount}
          totalResultsCount={totalResultsCount}
          searchListingsError={''}
          inboxItemsAreLoaded={inboxItemsAreLoaded}
          selectedFiltersCount={selectedFiltersCount}
          searchAllAndResetFilters={this.searchAllAndResetFilters}
        >
          {primaryFilters.map(config => {
            return (
              <FilterComponent
                key={`InboxFiltersPrimary.${config.id}`}
                idPrefix="InboxFiltersPrimary"
                filterConfig={config}
                activeQueryParams={activeQueryParams}
                initialValues={this.initialValues}
                getHandleChangedValueFn={this.getHandleChangedValueFn}
                showAsPopup
                contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
              />
            );
          })}
        </InboxFiltersPrimary>
        <InboxFiltersMobile
          className={css.inboxFiltersMobile}
          activeQueryParams={activeQueryParams}
          sortByComponent={sortBy('mobile')}
          resultsCount={resultsCount}
          inboxItemsAreLoaded={inboxItemsAreLoaded}
          searchListingsError={''}
          showAsModalMaxWidth={showAsModalMaxWidth}
          onManageDisableScrolling={onManageDisableScrolling}
          onOpenModal={onOpenModal}
          onCloseModal={onCloseModal}
          resetAll={this.resetAll}
          searchAllAndResetFilters={this.searchAllAndResetFilters}
          selectedFiltersCount={selectedFiltersCount}
          totalResultsCount={totalResultsCount}
          isSearchListingsFilter={false}
        >
          {filterConfig.map(config => {
            return (
              <FilterComponent
                key={`InboxFiltersMobile.${config.id}`}
                idPrefix="InboxFiltersMobile"
                filterConfig={config}
                activeQueryParams={activeQueryParams}
                initialValues={this.initialValues}
                getHandleChangedValueFn={this.getHandleChangedValueFn}
                liveEdit
                showAsPopup={false}
              />
            );
          })}
        </InboxFiltersMobile>
      </div>
    );
  }
}

InboxFiltersPanel.defaultProps = {
  className: null,
  rootClassName: null,
  resultsCount: 0,
  totalResultsCount: 0,
  filterConfig: config.custom.inboxFilters,
  sortConfig: config.custom.inboxSortConfig,
};

InboxFiltersPanel.propTypes = {
  className: string,
  rootClassName: string,
  activeQueryParams: object.isRequired,
  searchListingsError: propTypes.error,
  onManageDisableScrolling: func.isRequired,
  onOpenModal: func.isRequired,
  onCloseModal: func.isRequired,
  showAsModalMaxWidth: number.isRequired,
  filterConfig: propTypes.filterConfig,
  sortConfig: propTypes.sortConfig,
};

export default InboxFiltersPanel;
